import { Component, OnInit, OnDestroy } from '@angular/core';
import { ColDef, GridApi, GridReadyEvent, GridOptions, ApplyColumnStateParams } from 'ag-grid-community';
import { AgGridOptionsBuilder, AgGridFilterParams, AgGridColumns } from '../../../Compliance/AgGrid';
import { LeadDocketLogListAgGridDataSource } from './agGridDataSource';
import { WeissmanTimestampMsecFormatPipe } from '../../../UI-Lib/Pipes/Date-Format/date-formatting.pipe';
import { Subscription } from 'rxjs';
import { LeadDocketLogService } from '../leadDocketLog.service';
import { LeadDocketLogRepository } from '../leadDocketLog.repository';
import { LeadDocketLogListGridActionCellRendererComponent, LeadDocketLogListGridActionCellRendererParams } from './agGridActionCellRenderer.component';
import { WeissmanModalService } from '../../../Compliance/WeissmanModalService';
import { LeadDocketLogDetailsModalComponent, LeadDocketLogDetailsParams } from '../Lead-Docket-Log-Details/leadDocketLogDetailsModal.component';
import { BusyIndicatorService } from '../../../Busy-Indicator';
import { RestrictService, Roles } from '../../../Common/Permissions/restrict.service';
import { UpgradeNavigationServiceHandler } from '../../../Common/Routing/upgrade-navigation-handler.service';

@Component({
    selector: 'lead-docket-log-list',
    templateUrl: './leadDocketLogList.component.html',
    styleUrls: ['./leadDocketLogList.component.scss'],
    standalone: false
})
export class LeadDocketLogListComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _timestampPipe: WeissmanTimestampMsecFormatPipe,
        private readonly _leadDocketLogRepository: LeadDocketLogRepository,
        private readonly _leadDocketLogService: LeadDocketLogService,
        private readonly _modalService: WeissmanModalService,
        private readonly _busyService: BusyIndicatorService,
        private readonly _restrictService: RestrictService,
        private readonly _routerService: UpgradeNavigationServiceHandler) {
    }

    gridId: System.Guid = 'A1B2C3D4-E5F6-7890-ABCD-EF1234567890';
    isInitialized: boolean = false;
    gridOptions: GridOptions = new AgGridOptionsBuilder()
        .withColumnPinning()
        .buildDefault(this);
    gridApi: GridApi;

    private _gridDataSource: LeadDocketLogListAgGridDataSource;
    private _refreshSub: Subscription;

    get refreshing(): boolean {
        return this._gridDataSource && this._gridDataSource.isRefreshing;
    }

    ngOnInit(): void {
        if (!(this._restrictService.isInRole(Roles.ADMINOPERATIONSVIEW) || this._restrictService.isInRole(Roles.ADMINOPERATIONSEDIT))) {
            this._routerService.go('unauthorizedAccess', {});
            return;
        }

        this._refreshSub = this._leadDocketLogService.refresh$.subscribe(() => {
            this._refreshDataSource();
        });

        this.isInitialized = true;
    }

    ngOnDestroy(): void {
        this._refreshSub && this._refreshSub.unsubscribe();
    }

    onAgGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;

        const columns: ColDef[] = [
            {
                headerName: 'Summary ID',
                field: 'leadDocketSummaryLogId',
                width: AgGridColumns.textColumnSmallWidth,
                maxWidth: AgGridColumns.textColumnMedWidth
            },
            {
                headerName: 'Operation Type',
                field: 'operationType',
                width: AgGridColumns.textColumnMedWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
            },
            {
                headerName: 'Litigation Referral ID',
                field: 'litigationReferralId',
                width: AgGridColumns.textColumnSmallWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams
            },
            {
                headerName: 'Matter ID',
                field: 'matterId',
                width: AgGridColumns.textColumnMedWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
            },
            {
                headerName: 'Site ID',
                field: 'siteId',
                width: AgGridColumns.textColumnSmallWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams
            },
            {
                headerName: 'Start Time',
                field: 'startTime',
                width: AgGridColumns.textColumnMedWidth,
                filter: 'agDateColumnFilter',
                filterParams: AgGridFilterParams.dateFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
                valueFormatter: (params) => {
                    const model = params.data as Core.LeadDocketSummaryLogModel;
                    if (!(model && model.startTime)) {
                        return '';
                    }
                    return this._timestampPipe.transform(model.startTime);
                }
            },
            {
                headerName: 'End Time',
                field: 'endTime',
                width: AgGridColumns.textColumnMedWidth,
                filter: 'agDateColumnFilter',
                filterParams: AgGridFilterParams.dateFilterParamsWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
                valueFormatter: (params) => {
                    const model = params.data as Core.LeadDocketSummaryLogModel;
                    if (!(model && model.endTime)) {
                        return '';
                    }
                    return this._timestampPipe.transform(model.endTime);
                }
            },
            {
                headerName: 'Request Payload',
                field: 'requestPayloadJson',
                width: AgGridColumns.textColumnWidth,
                maxWidth: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                sortable: false
            },
            {
                headerName: 'Response Payload',
                field: 'responsePayloadJson',
                width: AgGridColumns.textColumnWidth,
                maxWidth: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                sortable: false
            },
            {
                headerName: '',
                field: 'actions',
                pinned: 'right',
                width: AgGridColumns.getActionColumnWidth(1),
                minWidth: AgGridColumns.getActionColumnWidth(1),
                maxWidth: AgGridColumns.getActionColumnWidth(1),
                suppressSizeToFit: true,
                suppressAutoSize: true,
                resizable: false,
                suppressColumnsToolPanel: true,
                lockPinned: true,
                sortable: false,
                cellRenderer: LeadDocketLogListGridActionCellRendererComponent,
                cellRendererParams: {
                    viewDetails: this._viewDetails.bind(this)
                } as LeadDocketLogListGridActionCellRendererParams
            }
        ];

        const defaultSortModel: ApplyColumnStateParams = {
            state: [{ colId: 'leadDocketSummaryLogId', sort: 'desc' }],
            defaultState: { sort: null }
        };

        this.gridApi.setGridOption('paginationPageSize', 100);
        this.gridApi.setGridOption('columnDefs', columns);
        this.gridApi.applyColumnState(defaultSortModel);
        this._setDataSource();
    }

    async refresh(): Promise<void> {
        const busyRef = this._busyService.show({ message: 'Loading' });

        try {
            await this._leadDocketLogService.refresh();
        } finally {
            busyRef.hide();
        }
    }

    private async _viewDetails(params: LeadDocketLogListGridActionCellRendererParams): Promise<void> {
        const leadDocketSummaryLog = params.data as Core.LeadDocketSummaryLogModel;
        if (!leadDocketSummaryLog) {
            return;
        }

        const detailParams: LeadDocketLogDetailsParams = {
            summaryLogId: leadDocketSummaryLog.leadDocketSummaryLogId
        };

        await this._modalService.showAsync(LeadDocketLogDetailsModalComponent, detailParams, 'modal-lg');
    }

    private _refreshDataSource(): void {
        if (!this._gridDataSource) {
            const success = this._setDataSource();
            if (!success) {
                return;
            }
        }

        this._gridDataSource.refresh();
    }

    private _setDataSource(): boolean {
        if (!this.gridApi || this._gridDataSource) {
            return;
        }

        this._gridDataSource = new LeadDocketLogListAgGridDataSource(this.gridApi, this._leadDocketLogRepository);
        this.gridApi.updateGridOptions({ datasource: this._gridDataSource });
        return true;
    }
}
